import { baseScopesRequiredPerField } from '@atlassian/ui-modifications-core/constants';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';

/**
 * Internal representation of project picker values used by UIM processing.
 * Contains the project ID that UIM stores and passes through to Jira's field system.
 */
export type ProjectPickerInternalValue = null | {
	/** Project ID (required) */
	id: string;
};

/**
 * Forge Public API format for setting project picker values.
 * Used by Forge apps when calling setValue() on project picker fields.
 */
export type ProjectPickerPublicSetValue = null | string;

/**
 * Forge Public API format for getting project picker values.
 * Returned to Forge apps when calling getValue() on project picker fields.
 */
export type ProjectPickerPublicGetValue = null | {
	/** The unique identifier of the selected project */
	projectId: string;
};

/**
 * Validates input for project picker setValue operations.
 *
 * This validator is called by the UIM platform during field validation in the submission pipeline
 * (platform/packages/ui-modifications/core/src/ui/root/fragment/apps-renderer/single-app-renderer/use-custom-bridge-methods/use-submit/use-fields-changes-validation/validate-field-changes/index.tsx).
 *
 * It only checks the input format (null or non-empty string) but does NOT validate if the string
 * represents a valid project ID. Project existence validation is handled by JFE (Jira Frontend)
 * which will fail silently if an invalid project is provided - this is considered by design.
 *
 * @param value - The input value to validate from app setValue calls
 * @returns True if the value is null (clears field) or a non-empty string (project ID)
 *
 * @example
 * ```typescript
 * projectPickerSetValueValidator(null)        // true - clears field
 * projectPickerSetValueValidator("10001")     // true - project ID
 * projectPickerSetValueValidator("")          // false - empty string rejected
 * projectPickerSetValueValidator(123)        // false - numbers rejected
 * ```
 */
export const projectPickerSetValueValidator = (
	value: unknown,
): value is ProjectPickerPublicSetValue => {
	return value === null || isNotEmptyString(value);
};

export const projectPickerScopes = baseScopesRequiredPerField;
